import React from 'react';
import { motion } from 'framer-motion';
import { Download, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';

const EASE = [0.16, 1, 0.3, 1];
const reveal = {
  initial: { opacity: 0, y: 26 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-70px' },
  transition: { duration: 0.75, ease: EASE },
};

/* ============================================================================
   Shared bespoke product-page sections (Ortho / Dent / Vet).
   Premium, bullet-free editorial layouts — sharp edges, hairline rules, no
   uniform card grids and no generic icons. The product accent colour
   (Ortho=blue, Dent=pink, Vet=green) flows through via the `accent` prop and a
   `--accent` CSS variable so hover/number/rule treatments stay on-brand.
   ========================================================================== */

const rgba = (hex, a) => {
  const m = hex.replace('#', '');
  const r = parseInt(m.substring(0, 2), 16);
  const g = parseInt(m.substring(2, 4), 16);
  const b = parseInt(m.substring(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
};

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const proxyDl = (url, name) => `${BACKEND}/api/download?url=${encodeURIComponent(url)}&name=${encodeURIComponent(name)}`;

const Eyebrow = ({ children, accent }) => (
  <div className="mb-5 flex items-center gap-3">
    <span
      aria-hidden="true"
      className="h-px w-10"
      style={{ background: `linear-gradient(to right, ${rgba(accent, 0.75)}, transparent)` }}
    />
    <span
      className="font-display text-[11px] font-bold uppercase tracking-[0.34em]"
      style={{ color: accent, textShadow: `0 0 20px ${rgba(accent, 0.45)}` }}
    >
      {children}
    </span>
  </div>
);

/* ---- Innovative Properties — editorial newspaper columns ---------------- */
export const ProductPropertiesEditorial = ({
  properties,
  eyebrow = 'Innovative Properties',
  heading,
  testidPrefix,
  bg = 'bg-slate-950',
  footnote,
  accent = '#3b82f6',
}) => (
  <section className={`py-16 ${bg}`} style={{ '--accent': accent }}>
    <div className="max-w-6xl mx-auto px-6 lg:px-8">
      <motion.div {...reveal} className="mb-12 max-w-2xl">
        <Eyebrow accent={accent}>{eyebrow}</Eyebrow>
        <h2 className="font-serif text-4xl italic leading-[1.05] tracking-tight text-white md:text-5xl">{heading}</h2>
      </motion.div>
      <div className="columns-1 gap-x-12 md:columns-2 lg:columns-3">
        {properties.map((p, i) => (
          <div
            key={i}
            data-testid={`${testidPrefix}-prop-${i}`}
            className="group break-inside-avoid border-t border-white/10 pb-10 pt-5"
          >
            <div className="mb-3 flex items-center gap-3">
              <span className="font-mono text-xs font-bold tabular-nums" style={{ color: accent }}>{String(i + 1).padStart(2, '0')}</span>
              <span className="h-px w-8 transition-all duration-500 group-hover:w-14" style={{ backgroundColor: rgba(accent, 0.6) }} />
            </div>
            <h3 className="mb-2 text-lg font-bold leading-snug text-white transition-colors duration-300 group-hover:text-[color:var(--accent)]">{p.title}</h3>
            <p className="text-sm leading-relaxed text-slate-400">{p.desc}</p>
          </div>
        ))}
      </div>
      {footnote && <p className="mt-8 text-xs text-slate-400">{footnote}</p>}
    </div>
  </section>
);

/* ---- Certifications & Patents — overlapping credential plaques ---------- */
export const ProductCertifications = ({
  items,
  testidPrefix,
  heading,
  bg = 'bg-slate-950',
  accent = '#3b82f6',
}) => (
  <section className={`py-16 ${bg}`} style={{ '--accent': accent }}>
    <div className="max-w-5xl mx-auto px-6 lg:px-8">
      <motion.h2 {...reveal} className="mb-12 text-center font-serif text-4xl italic tracking-tight text-white md:text-5xl">
        {heading || (<>Certifications &amp; <span style={{ color: accent }}>Patents</span></>)}
      </motion.h2>
      <div className="flex flex-col items-stretch gap-5 sm:flex-row sm:justify-center sm:gap-0">
        {items.map((c, i) => (
          <div
            key={i}
            data-testid={`${testidPrefix}-cert-${i}`}
            className="group relative flex min-h-[15rem] flex-1 flex-col justify-between overflow-hidden border border-white/20 bg-slate-900/60 p-7 backdrop-blur-xl transition-all duration-500 hover:z-10 hover:-translate-y-2 hover:border-[color:var(--accent)] sm:-ml-px sm:max-w-xs sm:first:ml-0"
          >
            <span aria-hidden="true" className="pointer-events-none absolute -right-5 -top-9 select-none text-[8rem] font-black leading-none" style={{ color: rgba(accent, 0.07) }}>{c.acr}</span>
            <span className="relative text-5xl font-light tracking-tight" style={{ color: accent }}>{c.acr}</span>
            <div className="relative">
              <h3 className="mb-1.5 text-lg font-bold text-white">{c.title}</h3>
              <p className="mb-2 font-mono text-xs" style={{ color: accent }}>{c.code}</p>
              <p className="text-sm leading-relaxed text-slate-400">{c.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ---- Published Research — high-end academic citation rows --------------- */
export const ProductPublications = ({
  publications,
  testidPrefix,
  dl,
  eyebrow = 'Scientific Research',
  heading,
  bg = 'bg-slate-950',
  countLabel,
  accent = '#3b82f6',
}) => (
  <section className={`py-16 ${bg}`} data-testid={`${testidPrefix}-publications`} style={{ '--accent': accent }}>
    <div className="max-w-5xl mx-auto px-6 lg:px-8">
      <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
        <motion.div {...reveal} className="max-w-2xl">
          <Eyebrow accent={accent}>{eyebrow}</Eyebrow>
          <h2
            className="font-serif text-4xl italic leading-[1.05] tracking-tight text-white md:text-5xl"
            style={{ textShadow: `0 0 60px ${rgba(accent, 0.18)}` }}
          >
            {heading || (<>Published <span style={{ color: accent }}>Research</span></>)}
          </h2>
        </motion.div>
        <span data-testid={`${testidPrefix}-pub-count`} className="font-mono text-xs uppercase tracking-[0.18em] text-slate-500">
          {countLabel || `${String(publications.length).padStart(2, '0')} peer-reviewed papers`}
        </span>
      </div>
      <div className="border-t border-white/10">
        {publications.map((pub, i) => (
          <div
            key={i}
            data-testid={`${testidPrefix}-pub-${i}`}
            className="group relative grid items-baseline gap-x-8 gap-y-2 border-b border-white/10 py-6 transition-colors duration-200 hover:bg-white/[0.02] md:grid-cols-[2rem_1fr_auto]"
          >
            <span className="font-mono text-xs tracking-widest text-slate-500">{String(i + 1).padStart(2, '0')}</span>
            <div className="min-w-0">
              <h3 className="text-base font-semibold leading-snug text-white transition-colors group-hover:text-[color:var(--accent)]">
                {pub.title}
                {pub.isNew && (
                  <span className="ml-2 inline-block align-middle bg-[#E87A20] px-2 py-0.5 text-[10px] font-black tracking-wider text-white">NEW 2026</span>
                )}
              </h3>
              <p className="mt-1 text-sm text-slate-400">{pub.authors}</p>
              {(pub.journal || pub.year) && (
                <p className="mt-1 text-[15px] italic" style={{ color: accent, fontFamily: "'Cormorant Garamond', serif" }}>
                  {[pub.journal, pub.year].filter(Boolean).join(' · ')}
                </p>
              )}
            </div>
            <div className="transition-opacity duration-300 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100">
              {pub.pdfUrl ? (
                <Button asChild size="sm" variant="ghost" className="font-bold text-xs hover:bg-white/5" style={{ color: accent }}>
                  <a href={proxyDl(pub.pdfUrl, pub.title.substring(0, 40))} download data-testid={`${testidPrefix}-pub-dl-${i}`}>
                    <Download className="w-3.5 h-3.5 mr-1.5" /> PDF
                  </a>
                </Button>
              ) : (
                <Button asChild size="sm" variant="ghost" className="text-xs hover:bg-white/5" style={{ color: accent }}>
                  <a href={pub.externalUrl} target="_blank" rel="noopener noreferrer" data-testid={`${testidPrefix}-pub-src-${i}`}><ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Source</a>
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);
